import React from "react";
import { useAppContext } from "@/context/app";
import { fetchWithAuth } from "@/utils/FetchUtils";

type UseApiHook = {
  token?: string;
  apiBase: string;
  buildUri: <Q = Record<string, string>>(path: string, query?: Q) => string;
  apiPost: <B = unknown, R = unknown>(path: string, body: B) => Promise<R>;
  apiPatch: <B = unknown, R = unknown>(path: string, body: B) => Promise<R>,
  apiPut: <B = unknown, R = unknown>(path: string, body: B) => Promise<R>;
  apiGet: <R = unknown, Q = Record<string, string>>(path: string, query?: Q) => Promise<R>;
  apiDelete: <R = unknown>(path: string) => Promise<R>;
};

const base = `${import.meta.env.VITE_REST_API_URI}`;

const useApi = (apiBase: string = base): UseApiHook => {
	const { auth: { user } } = useAppContext();
	const token = user?.token

	const buildUri = <Q = Record<string, string>>(path: string, query?: Q): string => {
		const isFullUrl = /^(https?:\/\/)/.test(path);
    const fullUrl = isFullUrl ? path : `${apiBase}${path}`;
    
		const keys: Array<keyof Q> = query ? (Object.keys(query) as Array<keyof Q>) : []
		if (query && keys.length > 0) {
			const params = new URLSearchParams();

			for (const k of keys) {
				if (query[k]) {
					params.append(String(k), String(query[k]));
				}
			}
			return fullUrl.concat(`?${params.toString()}`);
		}
		return fullUrl;
	}

	const apiPost = React.useCallback(<B, R>(path: string, body: B): Promise<R> => {
		return new Promise((resolve, reject) => {
			fetchWithAuth(buildUri(path), {
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${token}`
				}
			}).then(async (resp) => {
				if (resp.status === 200 || resp.status === 201) {
					return resolve(await resp.json())
				}
				return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
			}).catch((err) => reject(err));
		});
	}, [token]);

	const apiPut = React.useCallback(<B, R>(path: string, body: B): Promise<R> => {
		return new Promise((resolve, reject) => {
			fetchWithAuth(buildUri(path), {
				method: "PUT",
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${token}`
				}
			}).then(async (resp) => {
				if (resp.status === 200 || resp.status === 201) {
					return resolve(await resp.json())
				}
				return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
			}).catch((err) => reject(err));
		});
	}, [token]);

	const apiPatch = React.useCallback(<B, R>(path: string, body: B): Promise<R> => {
		return new Promise((resolve, reject) => {
			fetchWithAuth(buildUri(path), {
				method: "PATCH",
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${token}`
				}
			}).then(async (resp) => {
				if (resp.status === 200 || resp.status === 201) {
					return resolve(await resp.json())
				}
				return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
			}).catch((err) => reject(err));
		});
	}, [token]);

	const apiGet = React.useCallback(<R, Q = Record<string, string>>(path: string, query?: Q): Promise<R> => {
		return new Promise((resolve, reject) => {
			fetchWithAuth(buildUri(path, query), {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`
				}
			}).then(async (resp) => {
				if (resp.status === 200 || resp.status === 201) {
					return resolve(await resp.json())
				}
				return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
			}).catch((err) => reject(err));
		});
	}, [token]);

	const apiDelete = React.useCallback(<R>(path: string): Promise<R> => {
		return new Promise((resolve, reject) => {
			fetchWithAuth(buildUri(path), {
				method: "DELETE",
				headers: {
					Authorization: `Bearer ${token}`
				}
			}).then(async (resp) => {
				if (resp.status === 200 || resp.status === 201) {
					return resolve(await resp.json())
				}
				return reject(new Error(`Status: [${resp.status}] ${await resp.json()}`));
			}).catch((err) => reject(err));
		});
	}, [token]);

	return {
		token,
		apiBase,
		buildUri,
		apiPost,
		apiPatch,
		apiPut,
		apiGet,
		apiDelete,
	};
};

export default useApi;
